// Voucher Status Enum
export enum VoucherStatus {
    Active = 0,
    Redeemed = 1,
    Expired = 2,
    Cancelled = 3,
    Revoked = 4
}

// Voucher Item Contribution DTO
export interface VoucherItemContributionDto {
    scrapItemId?: string;
    scrapItemName?: string;
    percentage?: number;
    contributionAmount?: number;
}

// Voucher DTO
export interface VoucherDto {
    id?: string;
    voucherConfigId?: string;
    voucherConfigName?: string;
    customerId: string;
    customerName: string;
    customerPhoneNumber: string;
    scrapCollectionId: string;
    voucherAmount?: number;
    minimumPurchaseAmount?: number;
    partnerId: string;
    partnerName: string;
    outletId?: string;
    outletName?: string;
    voucherCode: string;
    expiryDate?: string;
    status?: VoucherStatus;
    redeemedAt?: string;
    redemptionReference?: string;
    notes?: string;
    itemContributions?: VoucherItemContributionDto[];
}

// Claim Vouchers DTO
// Only client-provided fields - server handles outletId, outletName, partnerUserId, partnerUserName, redeemedAt
export interface ClaimVouchersDto {
    voucherIds: string[];
    redemptionReference?: string;
    notes?: string;
    billAmount?: number;
}

// Revoke Vouchers DTO
export interface RevokeVouchersDto {
    customerId: string;
    redemptionReference: string;
    notes?: string;
}

// Paginated Response DTO
export interface PaginatedResponse<T> {
    items: T[];
    totalCount: number;
    pageNumber: number;
    pageSize: number;
}

// Voucher Insights DTO
export interface VoucherInsightsDto {
    totalVouchers: number;
    totalActiveVouchers: number;
    totalRedeemedVouchers: number;
    totalRevokedVouchers: number;
    totalExpiredVouchers: number;
    totalCancelledVouchers: number;
    totalVoucherAmount: number;
    totalRedeemedAmount: number;
    totalMinPurchaseAmount: number;
}

// Helper function to get status label
export function getVoucherStatusLabel(status?: VoucherStatus): string {
    switch (status) {
        case VoucherStatus.Active:
            return 'Active';
        case VoucherStatus.Redeemed:
            return 'Redeemed';
        case VoucherStatus.Expired:
            return 'Expired';
        case VoucherStatus.Cancelled:
            return 'Cancelled';
        case VoucherStatus.Revoked:
            return 'Revoked';
        default:
            return 'Unknown';
    }
}

// Helper function to check if voucher is claimable
export function isVoucherClaimable(voucher: VoucherDto): boolean {
    if (voucher.status !== VoucherStatus.Active) {
        return false;
    }

    if (voucher.expiryDate) {
        const expiryDate = new Date(voucher.expiryDate);
        const now = new Date();
        return expiryDate > now;
    }

    return true;
}

// Partner Zone DTO
export interface PartnerZoneDto {
    id: string;
    name: string;
}

// Follow-up History Item DTO
export interface FollowUpHistoryItemDto {
    status: string;
    remarks: string;
    updatedByName: string;
    updatedAt: string;
}

// Pending Redemption Customer DTO
export interface PendingRedemptionCustomerDto {
    customerId: string;
    customerName: string;
    customerPhoneNumber: string;
    availableVouchersCount: number;
    totalVoucherAmount: number;
    totalMinPurchaseAmount: number;
    recentExpiryDate?: string;
    zoneId: string;
    zoneName: string;
    followUpStatus: string;
    lastContacted?: string;
    lastRemark: string;
    followUpHistory: FollowUpHistoryItemDto[];
}

// Save Follow-up DTO
export interface SaveFollowUpDto {
    customerId: string;
    status: string;
    remarks: string;
}

// Pending Redemption Filter Parameters
export interface PendingRedemptionFilterParams {
    customerName?: string;
    customerPhoneNumber?: string;
    zoneIds?: string;
    status?: string;
    sortBy?: string;
    sortDescending?: boolean;
    pageIndex?: number;
    pageSize?: number;
}

